import { PhotoResult } from '../types/photo';

/**
 * How a vehicle photo is framed, everywhere it appears.
 *
 * A car is a wide object, photographed at whatever ratio the operator's source happens to be. The
 * registration form squeezed it into a fixed 110px-tall banner with `object-cover`, so a 984×732
 * photo lost its roof and its number plate — the two things anyone opens a vehicle photo for.
 *
 * Two decisions live here so every surface makes the same one:
 *   - the operator frames the photo themselves, in the same cropper used for a visitor's photo,
 *     at a fixed ratio so cards line up;
 *   - what they framed is then shown WHOLE. Re-cropping at display time would silently undo the
 *     operator's decision, which is the bug above.
 */

/** Landscape, because cars are. Configures the cropper. */
export const VEHICLE_PHOTO_ASPECT = 3 / 2;

/** The same ratio as a Tailwind class, for the box the photo sits in. */
export const VEHICLE_PHOTO_ASPECT_CLASS = 'aspect-[3/2]';

/**
 * `object-fill`, never `object-cover`.
 *
 * The frame is filled edge to edge and nothing is cut — covering would crop a photo the operator
 * already framed, which is how the roof and the number plate used to disappear. The cost is
 * proportions on legacy photos stored at other ratios; a photo cropped in the editor is framed at
 * this ratio and fills the box exactly.
 */
export const VEHICLE_PHOTO_FIT_CLASS = 'object-fill';

const EXTENSIONS: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

/**
 * The cropper's blob, as the File the vehicle photo endpoint expects.
 *
 * The registration form used to upload the operator's original File untouched, which is why it
 * never passed through a cropper at all.
 */
export function vehiclePhotoFile(result: PhotoResult, plate?: string): File {
  const type = result.blob.type || 'image/jpeg';
  const extension = EXTENSIONS[type] ?? 'jpg';
  // The photo can be chosen before the plate is typed, so a blank plate must still name a file.
  const name = (plate ?? '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase() || 'veiculo';
  return new File([result.blob], `${name}.${extension}`, { type });
}

/** What clicking a vehicle photo slot can do. */
export type VehiclePhotoAction = 'recrop' | 'upload' | 'remove';

/**
 * The actions a photo slot offers.
 *
 * The slot had exactly one behaviour: open a file picker. For a vehicle already registered that
 * is the wrong default — the common correction is "the photo is fine, it is just framed badly",
 * and the only way to do that was to find the original file again and re-upload it.
 *
 * `recrop` comes first because it is the common case. With no photo there is nothing to re-crop
 * or remove, and a menu of one is just a slower file picker.
 */
export function vehiclePhotoActions(hasPhoto: boolean): VehiclePhotoAction[] {
  return hasPhoto ? ['recrop', 'upload', 'remove'] : ['upload'];
}

/**
 * A stored photo, as a data URL the cropper can open.
 *
 * Cropping draws to a canvas, and a canvas painted from a CROSS-ORIGIN image is tainted —
 * `toBlob` then throws and the operator is left with a dead "Salvar" and no explanation. Fetching
 * the bytes first sidesteps that: the data URL is same-origin by definition.
 *
 * `fetchImpl` is a parameter so this is testable without a network.
 */
export async function imageUrlToDataUrl(
  url: string,
  fetchImpl: typeof fetch = fetch,
): Promise<string> {
  // Already inline — a photo picked in this session. Re-fetching it would be absurd.
  if (url.startsWith('data:')) return url;

  const response = await fetchImpl(url, { credentials: 'include' });
  if (!response.ok) {
    throw new Error(`Não foi possível carregar a foto (${response.status})`);
  }
  const blob = await response.blob();

  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Não foi possível ler a foto'));
    reader.readAsDataURL(blob);
  });
}
